/* eslint-disable react/jsx-props-no-spreading -- used to pass all the props */
import React from 'react';
import { type MenuOption } from '@saviynt/design-system';

import { type AdvancedFilterState } from '../../../types';
import { type SodAdvancedFiltersSharedFunc } from '../SodControls';

import { DATE_BASED_FILTERS, RANGE_BASED_FILTERS } from './constants';
import SodDateFilter from './SodDateFilter';
import SodRiskScoreFilter from './SodRiskScoreFilter';

export interface SodCustomFilterProps
  extends Pick<
    SodAdvancedFiltersSharedFunc,
    'handleOptionsClick' | 'handleFilterChange'
  > {
  filter: AdvancedFilterState;
  activeOptions: MenuOption[];
  footerLink1Text: string;
  footerLink2Text: string;
  enabledFilterValue?: string | null;
  setEnabledFilterValue?: (value: string | null) => void;
  isDarkTheme?: boolean;
  isDisabled?: boolean;
}

/**
 * Returns a custom filter component based on the provided filter key
 *
 * @param {string} filterKey - The key identifying which custom filter to return
 * @param {SodRiskScoreFilterProps} [props={}] - Props to be passed to the filter component
 * @returns {React.ReactElement} The corresponding filter component
 *
 * @example
 * // Returns a SodRiskScoreFilter component
 * getCustomFilter('RISKSCORE', { someProps });
 *
 * // Returns a default span element
 * getCustomFilter('UNKNOWN_KEY');
 */
export const getCustomFilter = (
  filterKey: string,
  props: SodCustomFilterProps = {} as SodCustomFilterProps
): React.ReactElement => {
  // Not ready from BE
  const isRangeFilter = Boolean(RANGE_BASED_FILTERS.includes(filterKey));

  if (isRangeFilter) {
    return <SodRiskScoreFilter {...props} />;
  }

  const isDateFilter = Boolean(DATE_BASED_FILTERS.includes(filterKey));

  if (isDateFilter) {
    return <SodDateFilter {...props} />;
  }

  return <span>No available filter!</span>;
};

export default getCustomFilter;
